import { useDroppable } from '@dnd-kit/core';
import {
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import { blockHasChildren, isInlineEditable } from './blockSchema.js';
import { useEditor } from './editorStore.js';
import { useEditorTheme } from './ThemeContext.jsx';
import { sanitizeRichText } from './sanitize.js';
import RichTextToolbar from './RichTextToolbar.jsx';

// ── public entry ─────────────────────────────────────────────────────────
// Read-only render uses ReadOnlyTree; editable render uses SortableTree.
export default function BlockRenderer({ blocks, editable, selectedId, onSelect }) {
  if (!editable) return <ReadOnlyTree blocks={blocks} />;
  return (
    <EditableTree
      blocks={blocks}
      parentId="__root__"
      selectedId={selectedId}
      onSelect={onSelect}
    />
  );
}

// ── editable tree (sortable + droppable) ─────────────────────────────────
function EditableTree({ blocks, parentId, selectedId, onSelect }) {
  if (blocks.length === 0) {
    return <EmptyDropZone parentId={parentId} />;
  }
  return (
    <SortableContext
      items={blocks.map((b) => b.id)}
      strategy={verticalListSortingStrategy}
    >
      {blocks.map((block, index) => (
        <SortableBlockNode
          key={block.id}
          block={block}
          parentId={parentId}
          index={index}
          selectedId={selectedId}
          onSelect={onSelect}
        />
      ))}
      <AppendDropZone parentId={parentId} />
    </SortableContext>
  );
}

function SortableBlockNode({ block, parentId, index, selectedId, onSelect }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
    active,
  } = useSortable({
    id: block.id,
    data: { source: 'block', parentId, index, type: block.type, blockId: block.id },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isSelected = selectedId === block.id;
  // Show an insertion line above this block when something is being dragged
  // over it from elsewhere (not when dragging the block itself).
  const showInsertLine = isOver && active && active.id !== block.id;

  function onClick(e) {
    e.stopPropagation();
    onSelect?.(block.id);
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={onClick}
      data-block-id={block.id}
      data-block-type={block.type}
      data-container={blockHasChildren(block.type) ? 'true' : undefined}
      className={clsx(
        'relative outline-2 outline-offset-2 transition',
        isDragging && 'opacity-30',
        isSelected ? 'outline outline-brand-500' : 'hover:outline hover:outline-brand-300/60',
      )}
    >
      {showInsertLine && <InsertLine />}
      {isSelected && !isDragging && (
        <BlockHandle
          listeners={listeners}
          attributes={attributes}
          label={block.type}
          blockId={block.id}
        />
      )}
      <BlockBody
        block={block}
        editable
        parentId={parentId}
        selectedId={selectedId}
        onSelect={onSelect}
      />
    </div>
  );
}

function InsertLine() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute -top-1 left-0 right-0 z-10 h-0.5 rounded-full bg-brand-500 shadow-[0_0_0_3px_rgba(91,108,255,0.18)]"
    />
  );
}

function BlockHandle({ listeners, attributes, label, blockId }) {
  const duplicateBlock = useEditor((s) => s.duplicateBlock);
  const removeBlock = useEditor((s) => s.removeBlock);
  const moveBlockBy = useEditor((s) => s.moveBlockBy);

  // Buttons stop propagation so clicking them doesn't bubble up to the
  // SortableBlockNode (would re-fire selection) or the canvas (would
  // deselect).
  const stop = (e) => e.stopPropagation();

  return (
    <div className="pointer-events-none absolute -top-7 left-0 z-20 flex items-center gap-1">
      <span
        {...listeners}
        {...attributes}
        className="pointer-events-auto flex cursor-grab items-center gap-1 rounded bg-brand-500 px-2 py-0.5 text-[10px] font-semibold uppercase text-white shadow-sm active:cursor-grabbing"
        title="Drag to reorder"
      >
        <span aria-hidden>⠿</span>
        <span>{label}</span>
      </span>
      <div className="pointer-events-auto flex items-center gap-0.5 rounded border border-ink-200 bg-white px-0.5 py-0.5 text-ink-600 shadow-sm">
        <ActionBtn onClick={(e) => { stop(e); moveBlockBy(blockId, -1); }} title="Move up">↑</ActionBtn>
        <ActionBtn onClick={(e) => { stop(e); moveBlockBy(blockId, +1); }} title="Move down">↓</ActionBtn>
        <ActionBtn onClick={(e) => { stop(e); duplicateBlock(blockId); }} title="Duplicate (Ctrl+D)">⎘</ActionBtn>
        <ActionBtn onClick={(e) => { stop(e); removeBlock(blockId); }} title="Delete" danger>✕</ActionBtn>
      </div>
    </div>
  );
}

function ActionBtn({ children, danger, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className={clsx(
        'h-6 w-6 rounded text-xs leading-none transition',
        danger ? 'hover:bg-red-50 hover:text-red-600' : 'hover:bg-ink-100',
      )}
    >
      {children}
    </button>
  );
}

// Two flavors of drop zone — empty container and end-of-list "append".
function EmptyDropZone({ parentId }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `__empty__-${parentId}`,
    data: { kind: 'empty', parentId },
  });
  return (
    <div
      ref={setNodeRef}
      className={clsx(
        'm-2 grid min-h-[80px] place-items-center rounded-lg border-2 border-dashed text-xs transition',
        isOver
          ? 'border-brand-500 bg-brand-50 text-brand-700'
          : 'border-ink-200 text-ink-400',
      )}
    >
      Drop blocks here
    </div>
  );
}

function AppendDropZone({ parentId }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `__append__-${parentId}`,
    data: { kind: 'append', parentId },
  });
  return (
    <div
      ref={setNodeRef}
      className={clsx(
        'mx-2 my-1 h-2 rounded transition',
        isOver ? 'h-3 bg-brand-500/40' : 'bg-transparent',
      )}
    />
  );
}

// ── read-only tree (used by preview modal + non-editor contexts) ─────────
function ReadOnlyTree({ blocks }) {
  return (
    <>
      {blocks.map((b) => (
        <BlockBody key={b.id} block={b} />
      ))}
    </>
  );
}

// ── shared: render the visual body of any block type ─────────────────────
function BlockBody({ block, editable, selectedId, onSelect }) {
  const { type, props = {}, children = [] } = block;

  switch (type) {
    case 'section':
      return (
        <SectionBlock
          props={props}
          childBlocks={children}
          block={block}
          editable={editable}
          selectedId={selectedId}
          onSelect={onSelect}
        />
      );
    case 'columns':
      return (
        <ColumnsBlock
          props={props}
          childBlocks={children}
          block={block}
          editable={editable}
          selectedId={selectedId}
          onSelect={onSelect}
        />
      );
    case 'column':
      // Reached only by the DragPreview (which renders a single block). On
      // the canvas, columns are rendered via ColumnsBlock → ColumnBucket,
      // not through BlockBody.
      return <ReadOnlyTree blocks={children} />;
    case 'navbar':
      return <NavbarBlock props={props} editable={editable} />;
    case 'footer':
      return <FooterBlock props={props} editable={editable} />;
    case 'heading':
      return <HeadingBlock props={props} editable={editable} block={block} />;
    case 'text':
      return <TextBlock props={props} editable={editable} block={block} />;
    case 'quote':
      return <QuoteBlock props={props} />;
    case 'image':
      return <ImageBlock props={props} />;
    case 'video':
      return <VideoBlock props={props} />;
    case 'button':
      return <ButtonBlock props={props} editable={editable} />;
    case 'accordion':
      return <AccordionBlock props={props} editable={editable} />;
    case 'divider':
      return <DividerBlock props={props} />;
    case 'spacer':
      return <SpacerBlock props={props} />;
    default:
      return null;
  }
}

// ── containers ──────────────────────────────────────────────────────────
function SectionBlock({ props, childBlocks, editable, selectedId, onSelect, block }) {
  const theme = useEditorTheme();
  const style = props.style || {};
  return (
    <section
      style={{
        background: style.background || theme.colors.background,
        color: style.color || theme.colors.foreground,
        padding: paddingFor(style.padding),
      }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        {editable ? (
          <EditableTree
            blocks={childBlocks}
            parentId={block.id}
            selectedId={selectedId}
            onSelect={onSelect}
          />
        ) : (
          <ReadOnlyTree blocks={childBlocks} />
        )}
      </div>
    </section>
  );
}

function ColumnsBlock({ props, childBlocks, editable, selectedId, onSelect, block }) {
  // v0.4: columns are siblings sortable horizontally within their parent.
  // Each column body is its own vertical sortable context for inner content.
  const columns = childBlocks.filter((c) => c.type === 'column');
  const gap = Math.max(0, Number(props.gap) || 24);

  const grid = {
    display: 'grid',
    gridTemplateColumns: `repeat(${Math.max(columns.length, 1)}, minmax(0, 1fr))`,
    gap,
  };

  if (!editable) {
    return (
      <div style={grid}>
        {columns.map((col) => (
          <ReadOnlyColumn key={col.id} block={col} />
        ))}
      </div>
    );
  }

  return (
    <SortableContext
      items={columns.map((c) => c.id)}
      strategy={horizontalListSortingStrategy}
    >
      <div style={grid}>
        {columns.map((col, i) => (
          <SortableColumnBucket
            key={col.id}
            block={col}
            parentId={block.id}
            index={i}
            selectedId={selectedId}
            onSelect={onSelect}
          />
        ))}
      </div>
    </SortableContext>
  );
}

function ReadOnlyColumn({ block }) {
  return (
    <div>
      <ReadOnlyTree blocks={block.children || []} />
    </div>
  );
}

function SortableColumnBucket({ block, parentId, index, selectedId, onSelect }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
    active,
  } = useSortable({
    id: block.id,
    data: { source: 'block', parentId, index, type: 'column', blockId: block.id },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    minWidth: 0, // critical: lets grid columns shrink below intrinsic content
  };

  const showInsertLine = isOver && active && active.id !== block.id && active.data?.current?.type === 'column';

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-block-id={block.id}
      data-block-type="column"
      data-container="true"
      className="group/col relative"
    >
      {showInsertLine && <ColumnInsertLine />}
      <ColumnGrip listeners={listeners} attributes={attributes} dragging={isDragging} />
      <div className={isDragging ? 'opacity-30' : ''}>
        <EditableTree
          blocks={block.children || []}
          parentId={block.id}
          selectedId={selectedId}
          onSelect={onSelect}
        />
      </div>
    </div>
  );
}

// Small handle that only this element triggers a column drag from. The body
// remains a normal droppable so users can drop content INTO the column.
function ColumnGrip({ listeners, attributes, dragging }) {
  return (
    <div className="pointer-events-none absolute -top-3 left-1/2 z-10 -translate-x-1/2">
      <span
        {...listeners}
        {...attributes}
        className={clsx(
          'pointer-events-auto cursor-grab rounded-full border border-ink-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-ink-500 opacity-0 shadow-sm transition group-hover/col:opacity-100 active:cursor-grabbing',
          dragging && 'opacity-100',
        )}
        title="Drag to reorder column"
      >
        ⠿ column
      </span>
    </div>
  );
}

function ColumnInsertLine() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute -left-1.5 bottom-0 top-0 z-10 w-0.5 rounded-full bg-brand-500 shadow-[0_0_0_3px_rgba(91,108,255,0.18)]"
    />
  );
}

// ── chrome ──────────────────────────────────────────────────────────────
function NavbarBlock({ props, editable }) {
  const theme = useEditorTheme();
  const links = props.links || [];
  return (
    <nav
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 24,
        padding: '14px 24px',
        background: 'rgba(255,255,255,0.85)',
        borderBottom: '1px solid #ececf2',
      }}
    >
      <div style={{ fontWeight: 800, fontSize: 18 }}>{props.brand || 'Brand'}</div>
      <div style={{ display: 'flex', gap: 18, marginLeft: 8 }}>
        {links.map((l, i) => (
          <a
            key={i}
            href={editable ? '#' : l.href || '#'}
            onClick={editable ? (e) => e.preventDefault() : undefined}
            style={{ fontSize: 14, color: '#3a4258', textDecoration: 'none' }}
          >
            {l.label}
          </a>
        ))}
      </div>
      {props.cta?.label && (
        <a
          href={editable ? '#' : props.cta.href || '#'}
          onClick={editable ? (e) => e.preventDefault() : undefined}
          style={{
            marginLeft: 'auto',
            background: theme.colors.primary,
            color: '#fff',
            padding: '8px 14px',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            textDecoration: 'none',
          }}
        >
          {props.cta.label}
        </a>
      )}
    </nav>
  );
}

function FooterBlock({ props, editable }) {
  const groups = props.groups || [];
  return (
    <footer
      style={{
        background: '#0b0b0e',
        color: '#cfd3dc',
        padding: '48px 24px',
      }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `1fr repeat(${Math.max(groups.length, 1)}, 1fr)`,
            gap: 32,
          }}
        >
          <div>
            <div style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>
              {props.brand || 'Brand'}
            </div>
            {props.tagline && (
              <p style={{ marginTop: 8, fontSize: 14, color: '#8a90a0' }}>{props.tagline}</p>
            )}
          </div>
          {groups.map((g, i) => (
            <div key={i}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: '#a4abbb',
                  marginBottom: 10,
                }}
              >
                {g.heading}
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
                {(g.links || []).map((l, j) => (
                  <li key={j}>
                    <a
                      href={editable ? '#' : l.href || '#'}
                      onClick={editable ? (e) => e.preventDefault() : undefined}
                      style={{ color: '#cfd3dc', fontSize: 14, textDecoration: 'none' }}
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div
          style={{
            marginTop: 36,
            paddingTop: 18,
            borderTop: '1px solid #1c1d24',
            color: '#6f7585',
            fontSize: 12,
          }}
        >
          {props.copyright}
        </div>
      </div>
    </footer>
  );
}

// ── typography ──────────────────────────────────────────────────────────
function HeadingBlock({ props, editable, block }) {
  const Tag = `h${Math.max(1, Math.min(6, Number(props.level) || 2))}`;
  const editing = editable && isInlineEditable(block.type);
  const baseStyle = { margin: '0 0 16px 0', fontWeight: 700, whiteSpace: 'pre-line' };
  if (editing) {
    return (
      <Tag style={baseStyle}>
        <Editable block={block} propKey="text" />
      </Tag>
    );
  }
  return (
    <Tag
      style={baseStyle}
      dangerouslySetInnerHTML={{ __html: props.text || 'Heading' }}
    />
  );
}

function TextBlock({ props, editable, block }) {
  const editing = editable && isInlineEditable(block.type);
  const baseStyle = { margin: '0 0 16px 0', lineHeight: 1.6, whiteSpace: 'pre-line' };
  if (editing) {
    return (
      <p style={baseStyle}>
        <Editable block={block} propKey="text" />
      </p>
    );
  }
  return (
    <p
      style={baseStyle}
      dangerouslySetInnerHTML={{ __html: props.text || '' }}
    />
  );
}

function QuoteBlock({ props }) {
  return (
    <blockquote
      style={{
        margin: '0 0 16px 0',
        padding: '16px 20px',
        borderLeft: '4px solid currentColor',
        opacity: 0.9,
      }}
    >
      <div style={{ fontSize: 18, fontStyle: 'italic' }}>"{props.text}"</div>
      {props.author && (
        <div style={{ marginTop: 8, fontSize: 13, opacity: 0.7 }}>— {props.author}</div>
      )}
    </blockquote>
  );
}

// ── media ───────────────────────────────────────────────────────────────
function ImageBlock({ props }) {
  if (!props.src) {
    return (
      <div
        style={{
          padding: 24,
          border: '1px dashed #c8cdd6',
          borderRadius: 8,
          textAlign: 'center',
          color: '#65728a',
          fontSize: 13,
        }}
      >
        Image · add a URL in the inspector
      </div>
    );
  }
  return (
    <img
      src={props.src}
      alt={props.alt || ''}
      style={{ maxWidth: '100%', height: 'auto', borderRadius: 8 }}
    />
  );
}

function VideoBlock({ props }) {
  const embed = toEmbedUrl(props.url);
  if (!embed) {
    return (
      <div
        style={{
          padding: 24,
          border: '1px dashed #c8cdd6',
          borderRadius: 8,
          textAlign: 'center',
          color: '#65728a',
          fontSize: 13,
        }}
      >
        Video · paste a YouTube or Vimeo URL in the inspector
      </div>
    );
  }
  return (
    <figure style={{ margin: 0 }}>
      <div style={{ position: 'relative', paddingTop: '56.25%', borderRadius: 8, overflow: 'hidden' }}>
        <iframe
          src={embed}
          title={props.caption || 'Video'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
        />
      </div>
      {props.caption && (
        <figcaption style={{ marginTop: 8, fontSize: 13, opacity: 0.7 }}>{props.caption}</figcaption>
      )}
    </figure>
  );
}

// ── interactive ─────────────────────────────────────────────────────────
function ButtonBlock({ props, editable }) {
  const theme = useEditorTheme();
  const variant = props.variant || 'primary';
  const styles = {
    primary: { background: theme.colors.primary, color: '#fff' },
    secondary: { background: theme.colors.foreground, color: '#fff' },
    ghost: {
      background: 'transparent',
      color: theme.colors.primary,
      border: `1px solid ${theme.colors.primary}`,
    },
  }[variant] || {};

  return (
    <a
      href={editable ? '#' : props.href || '#'}
      onClick={editable ? (e) => e.preventDefault() : undefined}
      style={{
        display: 'inline-block',
        padding: '12px 22px',
        borderRadius: 8,
        fontWeight: 600,
        textDecoration: 'none',
        ...styles,
      }}
    >
      {props.label || 'Button'}
    </a>
  );
}

function AccordionBlock({ props, editable }) {
  const items = props.items || [];
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {items.map((item, i) => (
        <details
          key={i}
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            padding: '10px 14px',
            background: 'rgba(255,255,255,0.7)',
          }}
          {...(editable && i === 0 ? { open: true } : {})}
        >
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
            {item.question}
          </summary>
          <div style={{ marginTop: 8, fontSize: 14, opacity: 0.8 }}>{item.answer}</div>
        </details>
      ))}
    </div>
  );
}

// ── utility ─────────────────────────────────────────────────────────────
function DividerBlock({ props }) {
  return (
    <hr
      style={{
        border: 0,
        borderTop: `${Number(props.thickness) || 1}px solid ${props.color || '#e5e7eb'}`,
        margin: '24px 0',
      }}
    />
  );
}

function SpacerBlock({ props }) {
  return <div style={{ height: Math.max(0, Number(props.height) || 0) }} />;
}

// ── inline editable text ────────────────────────────────────────────────
// A ref-managed contentEditable that stores sanitized HTML. We never
// re-render its innerHTML from React while it has focus — that would
// clobber the caret. The DOM is treated as the source of truth between
// focus and blur; on blur we sanitize and commit back into the store.
// External changes (undo, AI replace) are applied via the effect when the
// React value diverges from the DOM AND the element isn't focused.
//
// Editing model:
// - Enter inserts a newline (default browser behavior, persisted as <br>)
// - Esc blurs / commits
// - Text selection surfaces a floating toolbar (B / I / link / unlink)
// - Output is sanitized to a small allowlist (b, strong, i, em, a, br)
function Editable({ block, propKey }) {
  const ref = useRef(null);
  const updateBlockProps = useEditor((s) => s.updateBlockProps);
  const initial = block.props?.[propKey] ?? '';

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if (el.innerHTML !== initial) el.innerHTML = initial;
  }, [initial]);

  function onBlur() {
    const raw = ref.current?.innerHTML ?? '';
    const clean = sanitizeRichText(raw);
    if (clean !== initial) updateBlockProps(block.id, propKey, clean);
  }

  function onKeyDown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      ref.current?.blur();
    }
  }

  return (
    <>
      <span
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        style={{ whiteSpace: 'pre-line' }}
        className="rounded outline-none focus:ring-2 focus:ring-brand-300/60"
      />
      <RichTextToolbar containerRef={ref} />
    </>
  );
}

// ── helpers ─────────────────────────────────────────────────────────────
function paddingFor(size) {
  switch (size) {
    case 'sm': return '24px 24px';
    case 'md': return '48px 24px';
    case 'lg': return '72px 24px';
    case 'xl': return '120px 24px';
    default: return '48px 24px';
  }
}

function toEmbedUrl(url) {
  if (!url) return '';
  try {
    const u = new URL(url);
    const host = u.hostname.replace('www.', '');
    if (host === 'youtu.be') return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    if (host.endsWith('youtube.com')) {
      const id = u.searchParams.get('v') || u.pathname.split('/').pop();
      return id ? `https://www.youtube.com/embed/${id}` : '';
    }
    if (host.endsWith('vimeo.com')) {
      const id = u.pathname.split('/').filter(Boolean).pop();
      return id ? `https://player.vimeo.com/video/${id}` : '';
    }
    return url;
  } catch {
    return '';
  }
}
